import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, inject, input, output } from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule, IconModule } from '@pinup-teams/common';
import { environment } from '@fb/environments';

import { SupportCircleImageComponent } from '../support-circle-image/support-circle-image.component';
import { AiChatFormDialog } from '../ai-chat-form/ai-chat-form.dialog';
import { SupportFormDialog } from '../support-form/support-form.dialog';
import { FinanceDialog } from '../finance-dialog/finance.dialog';
import { AiChatFormService } from '../ai-chat-form/ai-chat-form.service';
import { TranslateInPipe } from '../../pipes/translate-in.pipe';
import {
  FloatingButtonAction,
  FloatingButtonActionType,
  SupportCategories,
} from '../../models';
import { ItSupportDialog } from '../it-support-dialog/it-support.dialog';

@Component({
  selector: 'floating-button',
  standalone: true,
  imports: [
    SupportCircleImageComponent,
    OverlayModule,
    TranslateModule,
    IconModule,
    ButtonModule,
    TranslateInPipe,
    NgTemplateOutlet,
  ],
  providers: [
    AiChatFormDialog,
    SupportFormDialog,
    FinanceDialog,
    AiChatFormService,
    ItSupportDialog,
  ],
  templateUrl: './floating-button.component.html',
  styleUrl: './floating-button.component.scss',
})
export class FloatingButtonComponent {
  #aiChatFormDialog = inject(AiChatFormDialog);
  #supportFormDialog = inject(SupportFormDialog);
  #itSupportFormDialog = inject(ItSupportDialog);
  #financeDialog = inject(FinanceDialog);
  #aiChatFormService = inject(AiChatFormService);
  #destroyRef = inject(DestroyRef);

  categories = SupportCategories;
  actionType = FloatingButtonActionType;
  #dryRequestCompleted = false;

  blockBotButton = input<boolean>(false);
  actions = input.required<FloatingButtonAction[]>();
  popupOpened = output<boolean>();

  overlayOpened = false;

  aiChatButtonIcon = `${environment.shortName}-ai-chat-button`;

  openAiChat(): void {
    this.popupOpened.emit(true);
    this.#aiChatFormDialog
      .open()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.popupOpened.emit(false);
      });
  }

  openSupport(category: SupportCategories): void {
    this.popupOpened.emit(true);
    this.#supportFormDialog
      .open(category)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.popupOpened.emit(false);
      });
  }

  openFinanceDialog(): void {
    this.popupOpened.emit(true);
    this.#financeDialog
      .open()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.popupOpened.emit(false);
      });
  }

  openITSupportDialog(): void {
    this.popupOpened.emit(true);
    this.#itSupportFormDialog
      .open()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.popupOpened.emit(false);
      });
  }

  openOverlay(): void {
    this.overlayOpened = !this.overlayOpened;

    if (!this.#dryRequestCompleted && !this.blockBotButton()) {
      this.#aiChatFormService
        .dryRequest()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.#dryRequestCompleted = true;
        });
    }
  }
}
